import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { assignmentsApi, coursesApi, learningPathsApi, orgApi, resultsApi } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';

type ReportType = 'completion' | 'incomplete' | 'department' | 'quiz-score' | 'training-hours';

type Props = { storeId?: string | null; onBack?: () => void };

const REPORT_LABELS: Record<ReportType, string> = {
  completion: 'Completion Report',
  incomplete: 'Incomplete Training Report',
  department: 'Department Summary',
  'quiz-score': 'Quiz Score Report',
  'training-hours': 'Training Hours Report',
};

const HISTORY_KEY = 'training_reports_history';

function formatDate(value?: string | null) {
  if (!value || value === 'N/A') return value || 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function hoursFromDuration(duration?: string | null) {
  if (!duration) return 0;
  const parts = duration.toLowerCase().match(/(\d+(?:\.\d+)?)\s*(hour|hr|h|minute|min|m)/g) || [];
  let minutes = 0;
  parts.forEach((part) => {
    const num = parseFloat(part);
    if (/(hour|hr|h)/.test(part)) minutes += num * 60;
    else minutes += num;
  });
  return minutes / 60;
}

function downloadFile(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

function labelize(key: string) {
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
}

const TrainingReportsPage: React.FC<Props> = ({ storeId, onBack }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [pathAssignments, setPathAssignments] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [reportType, setReportType] = useState<ReportType>('completion');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<any[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [employeeRows, assignmentRows, resultRows, pathRows, courseRows] = await Promise.all([
        orgApi.listEmployees(storeId ? { storeId } : undefined),
        assignmentsApi.list(),
        resultsApi.list(),
        learningPathsApi.listAssignments(),
        coursesApi.list(),
      ]);
      const employeeIds = new Set((employeeRows as any[]).map((employee) => employee.id));
      const scopedAssignments = storeId
        ? (assignmentRows as any[]).filter((item) => employeeIds.has(item.employeeId))
        : (assignmentRows as any[]);
      const assignmentIds = new Set(scopedAssignments.map((item) => item.id));
      setEmployees(employeeRows as any[]);
      setAssignments(scopedAssignments);
      setResults((resultRows as any[]).filter((item) => assignmentIds.has(item.assignmentId)));
      setPathAssignments(storeId ? (pathRows as any[]).filter((item) => employeeIds.has(item.employeeId)) : (pathRows as any[]));
      setCourses(courseRows as any[]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    load();
    try {
      setHistory(JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'));
    } catch {
      setHistory([]);
    }
  }, [load]);

  const departments = useMemo(
    () => Array.from(new Set(employees.map((employee) => employee.department).filter(Boolean))).sort(),
    [employees],
  );

  const filtered = useMemo(() => {
    let scopedAssignments = assignments;
    let scopedPaths = pathAssignments;
    let scopedResults = results;
    if (startDate) {
      const start = new Date(startDate).getTime();
      scopedAssignments = scopedAssignments.filter((item) => new Date(item.createdAt).getTime() >= start);
      scopedPaths = scopedPaths.filter((item) => !item.assignedDate || new Date(item.assignedDate).getTime() >= start);
      scopedResults = scopedResults.filter((item) => new Date(item.createdAt).getTime() >= start);
    }
    if (endDate) {
      const end = new Date(`${endDate}T23:59:59`).getTime();
      scopedAssignments = scopedAssignments.filter((item) => new Date(item.createdAt).getTime() <= end);
      scopedPaths = scopedPaths.filter((item) => !item.assignedDate || new Date(item.assignedDate).getTime() <= end);
      scopedResults = scopedResults.filter((item) => new Date(item.createdAt).getTime() <= end);
    }
    if (courseFilter !== 'all') {
      scopedAssignments = scopedAssignments.filter((item) => item.courseId === courseFilter);
      const ids = new Set(scopedAssignments.map((item) => item.id));
      scopedResults = scopedResults.filter((item) => ids.has(item.assignmentId));
    }
    if (departmentFilter !== 'all') {
      scopedAssignments = scopedAssignments.filter((item) => item.employee?.department === departmentFilter);
      scopedPaths = scopedPaths.filter((item) => item.employee?.department === departmentFilter);
    }
    return { assignments: scopedAssignments, paths: scopedPaths, results: scopedResults };
  }, [assignments, pathAssignments, results, startDate, endDate, courseFilter, departmentFilter]);

  const report = useMemo(() => {
    const completed = filtered.assignments.filter((item) => item.isTrainingComplete || item.status === 'completed');
    const completedPaths = filtered.paths.filter((item) => item.status === 'completed');
    if (reportType === 'completion') {
      const rows = [
        ...completed.map((item) => ({
          employee: item.employee?.name || 'Unknown',
          email: item.employee?.email || 'N/A',
          department: item.employee?.department || 'N/A',
          course: item.course?.title || 'Unknown Course',
          type: 'Course',
          completedDate: formatDate(item.trainingCompletedAt || item.createdAt),
        })),
        ...completedPaths.map((item) => ({
          employee: item.employee?.name || 'Unknown',
          email: item.employee?.email || 'N/A',
          department: item.employee?.department || 'N/A',
          course: item.learningPath?.title || 'Unknown Path',
          type: 'Learning Path',
          completedDate: formatDate(item.completedDate),
        })),
      ];
      return {
        headers: [
          ['Employee', 'employee'],
          ['Email', 'email'],
          ['Department', 'department'],
          ['Course/Path', 'course'],
          ['Type', 'type'],
          ['Completed Date', 'completedDate'],
        ],
        rows,
        summary: {
          totalCompletions: rows.length,
          courseCompletions: completed.length,
          pathCompletions: completedPaths.length,
        },
      };
    }
    if (reportType === 'incomplete') {
      const now = Date.now();
      const incomplete = filtered.assignments.filter((item) => !item.isTrainingComplete && item.status !== 'completed');
      const overduePaths = filtered.paths.filter((item) => item.status !== 'completed' && item.dueDate && new Date(item.dueDate).getTime() < now);
      const rows = [
        ...incomplete.map((item) => ({
          employee: item.employee?.name || 'Unknown',
          email: item.employee?.email || 'N/A',
          department: item.employee?.department || 'N/A',
          course: item.course?.title || 'Unknown Course',
          type: 'Course',
          assignedDate: formatDate(item.createdAt),
          daysOverdue: Math.floor((now - new Date(item.createdAt).getTime()) / 86400000),
        })),
        ...overduePaths.map((item) => ({
          employee: item.employee?.name || 'Unknown',
          email: item.employee?.email || 'N/A',
          department: item.employee?.department || 'N/A',
          course: item.learningPath?.title || 'Unknown Path',
          type: 'Learning Path',
          assignedDate: formatDate(item.assignedDate),
          daysOverdue: item.dueDate ? Math.floor((now - new Date(item.dueDate).getTime()) / 86400000) : 0,
        })),
      ];
      return {
        headers: [
          ['Employee', 'employee'],
          ['Email', 'email'],
          ['Department', 'department'],
          ['Course/Path', 'course'],
          ['Type', 'type'],
          ['Assigned Date', 'assignedDate'],
          ['Days Overdue', 'daysOverdue'],
        ],
        rows,
        summary: { totalIncomplete: rows.length, overdueCount: rows.filter((row) => row.daysOverdue > 0).length },
      };
    }
    if (reportType === 'department') {
      const map = new Map<string, any>();
      employees.forEach((employee) => {
        if (!employee.department) return;
        if (!map.has(employee.department)) {
          map.set(employee.department, { totalEmployees: 0, totalAssignments: 0, completedAssignments: 0, scoreTotal: 0, attempts: 0 });
        }
        map.get(employee.department).totalEmployees += 1;
      });
      filtered.assignments.forEach((item) => {
        const department = item.employee?.department;
        if (!department || !map.has(department)) return;
        const row = map.get(department);
        row.totalAssignments += 1;
        if (item.isTrainingComplete || item.status === 'completed') row.completedAssignments += 1;
      });
      filtered.results.forEach((result) => {
        const assignment = filtered.assignments.find((item) => item.id === result.assignmentId);
        const department = assignment?.employee?.department;
        if (!department || !map.has(department)) return;
        const row = map.get(department);
        row.scoreTotal += Number(result.score || 0);
        row.attempts += 1;
      });
      const rows = Array.from(map.entries()).map(([department, stats]) => ({
        department,
        totalEmployees: stats.totalEmployees,
        totalAssignments: stats.totalAssignments,
        completed: stats.completedAssignments,
        completionRate: stats.totalAssignments ? Math.round((stats.completedAssignments / stats.totalAssignments) * 100) : 0,
        avgScore: stats.attempts ? Math.round(stats.scoreTotal / stats.attempts) : 0,
        quizAttempts: stats.attempts,
      }));
      return {
        headers: [
          ['Department', 'department'],
          ['Total Employees', 'totalEmployees'],
          ['Total Assignments', 'totalAssignments'],
          ['Completed', 'completed'],
          ['Completion Rate (%)', 'completionRate'],
          ['Avg Score', 'avgScore'],
          ['Quiz Attempts', 'quizAttempts'],
        ],
        rows,
        summary: {
          totalDepartments: rows.length,
          totalEmployees: rows.reduce((sum, row) => sum + row.totalEmployees, 0),
        },
      };
    }
    if (reportType === 'quiz-score') {
      const rows = filtered.results.map((result) => {
        const assignment = filtered.assignments.find((item) => item.id === result.assignmentId) || result.assignment;
        return {
          course: assignment?.course?.title || 'Unknown Course',
          employee: assignment?.employee?.name || 'Unknown',
          score: result.score,
          passed: result.passed ? 'Yes' : 'No',
          date: formatDate(result.createdAt),
        };
      });
      const byCourse = new Map<string, any[]>();
      rows.forEach((row) => {
        const list = byCourse.get(row.course) || [];
        list.push(row);
        byCourse.set(row.course, list);
      });
      const summary: Record<string, any> = {};
      byCourse.forEach((list, course) => {
        const scores = list.map((row) => Number(row.score || 0));
        summary[course] = {
          totalAttempts: scores.length,
          averageScore: Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length),
          passRate: Math.round((list.filter((row) => row.passed === 'Yes').length / scores.length) * 100),
          minScore: Math.min(...scores),
          maxScore: Math.max(...scores),
        };
      });
      return {
        headers: [
          ['Course', 'course'],
          ['Employee', 'employee'],
          ['Score', 'score'],
          ['Passed', 'passed'],
          ['Date', 'date'],
        ],
        rows,
        summary,
      };
    }
    const byEmployee = new Map<string, any>();
    filtered.assignments.forEach((item) => {
      if (!item.employee) return;
      if (!byEmployee.has(item.employeeId)) {
        byEmployee.set(item.employeeId, {
          employee: item.employee.name,
          email: item.employee.email || 'N/A',
          department: item.employee.department || 'N/A',
          totalHours: 0,
          courses: new Set<string>(),
        });
      }
      const row = byEmployee.get(item.employeeId);
      if (item.course?.title && !row.courses.has(item.course.title)) {
        row.courses.add(item.course.title);
        row.totalHours += hoursFromDuration(item.course.duration);
      }
    });
    const rows = Array.from(byEmployee.values()).map((row) => ({
      employee: row.employee,
      email: row.email,
      department: row.department,
      totalHours: row.totalHours.toFixed(2),
      coursesCompleted: row.courses.size,
    }));
    return {
      headers: [
        ['Employee', 'employee'],
        ['Email', 'email'],
        ['Department', 'department'],
        ['Total Hours', 'totalHours'],
        ['Courses Completed', 'coursesCompleted'],
      ],
      rows,
      summary: {
        totalEmployees: rows.length,
        totalHours: rows.reduce((sum, row) => sum + Number(row.totalHours), 0).toFixed(2),
      },
    };
  }, [reportType, filtered, employees]);

  const saveHistory = () => {
    const entry = {
      id: String(Date.now()),
      reportType,
      generatedAt: new Date().toISOString(),
      generatedBy: user?.name || user?.email || 'Manager',
      config: { reportType, startDate, endDate, courseFilter, departmentFilter },
    };
    const next = [entry, ...history].slice(0, 50);
    setHistory(next);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  };

  const filename = `Training_Report_${reportType}_${new Date().toISOString().split('T')[0]}`;

  const exportCsv = () => {
    const lines = [
      report.headers.map(([label]) => `"${label}"`).join(','),
      ...report.rows.map((row: any) => report.headers.map(([, key]) => `"${String(row[key] ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    downloadFile(`${filename}.csv`, lines.join('\n'), 'text/csv');
  };

  const exportExcel = () => {
    const escape = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const sheet = (name: string, rows: string[][]) => {
      const body = rows.map((row) => `<Row>${row.map((cell) => `<Cell><Data ss:Type="String">${escape(cell)}</Data></Cell>`).join('')}</Row>`).join('');
      return `<Worksheet ss:Name="${name}"><Table>${body}</Table></Worksheet>`;
    };
    const summaryRows = [['Metric', 'Value'], ...Object.entries(report.summary).map(([key, value]) => [labelize(key), typeof value === 'object' ? JSON.stringify(value) : String(value)])];
    const dataRows = [report.headers.map(([label]) => label), ...report.rows.map((row: any) => report.headers.map(([, key]) => String(row[key] ?? '')))];
    const xml = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">${sheet('Summary', summaryRows)}${sheet('Data', dataRows)}</Workbook>`;
    downloadFile(`${filename}.xls`, xml, 'application/vnd.ms-excel');
  };

  const exportJson = () => {
    downloadFile(`${filename}.json`, JSON.stringify({
      reportType,
      generatedAt: new Date().toISOString(),
      generatedBy: user?.name || user?.email || 'Manager',
      summary: report.summary,
      data: report.rows,
    }, null, 2), 'application/json');
  };

  const exportPdf = () => {
    const popup = window.open('', '_blank');
    if (!popup) return;
    const head = report.headers.map(([label]) => `<th>${label}</th>`).join('');
    const body = report.rows.map((row: any) => `<tr>${report.headers.map(([, key]) => `<td>${row[key] ?? ''}</td>`).join('')}</tr>`).join('');
    const summary = Object.entries(report.summary).map(([key, value]) => `<p><strong>${labelize(key)}:</strong> ${typeof value === 'object' ? JSON.stringify(value) : value}</p>`).join('');
    popup.document.write(`<!DOCTYPE html><html><head><title>${REPORT_LABELS[reportType]}</title><style>body{font-family:Arial,sans-serif;padding:24px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f5f5f5}</style></head><body><h1>Training Completion Reports</h1><h2>${REPORT_LABELS[reportType]}</h2>${summary}<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table><script>window.print()</script></body></html>`);
    popup.document.close();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {onBack && (
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight={700}>Training Completion Reports</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => setHistoryOpen(true)}>Report History</Button>
          <IconButton color="primary" title="Refresh" onClick={load}><RefreshIcon /></IconButton>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Report Configuration</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Report Type</InputLabel>
                <Select value={reportType} label="Report Type" onChange={(e) => setReportType(e.target.value as ReportType)}>
                  {(Object.keys(REPORT_LABELS) as ReportType[]).map((key) => (
                    <MenuItem key={key} value={key}>{REPORT_LABELS[key]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
              <TextField fullWidth label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Course</InputLabel>
                <Select value={courseFilter} label="Course" onChange={(e) => setCourseFilter(e.target.value)}>
                  <MenuItem value="all">All Courses</MenuItem>
                  {courses.map((course) => <MenuItem key={course.id} value={course.id}>{course.title}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Department</InputLabel>
                <Select value={departmentFilter} label="Department" onChange={(e) => setDepartmentFilter(e.target.value)}>
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map((department) => <MenuItem key={department} value={department}>{department}</MenuItem>)}
                </Select>
              </FormControl>
              <Button fullWidth variant="contained" startIcon={<VisibilityIcon />} onClick={saveHistory} sx={{ mt: 1 }}>
                Preview Report
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6">Report Preview</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} disabled={report.rows.length === 0} onClick={exportPdf}>PDF</Button>
                  <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} disabled={report.rows.length === 0} onClick={exportExcel}>Excel</Button>
                  <Button size="small" variant="outlined" startIcon={<DescriptionIcon />} disabled={report.rows.length === 0} onClick={exportCsv}>CSV</Button>
                  <Button size="small" variant="outlined" startIcon={<CodeIcon />} disabled={report.rows.length === 0} onClick={exportJson}>JSON</Button>
                </Box>
              </Box>

              {report.rows.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
                  Configure and preview a report to see data
                </Typography>
              ) : (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>Summary</Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {Object.entries(report.summary).map(([key, value]) => (
                      typeof value === 'object' && value !== null ? (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography fontWeight={700} sx={{ mb: 1 }}>{key}</Typography>
                              <Typography variant="body2">Attempts: {(value as any).totalAttempts}</Typography>
                              <Typography variant="body2">Average: {(value as any).averageScore}%</Typography>
                              <Typography variant="body2">Pass rate: {(value as any).passRate}%</Typography>
                              <Typography variant="body2">Range: {(value as any).minScore}% – {(value as any).maxScore}%</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ) : (
                        <Grid item xs={6} sm={4} key={key}>
                          <Card variant="outlined" sx={{ textAlign: 'center' }}>
                            <CardContent>
                              <Typography variant="body2" color="text.secondary">{labelize(key)}</Typography>
                              <Typography variant="h5" color="primary" fontWeight={700}>{String(value)}</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      )
                    ))}
                  </Grid>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {report.headers.map(([label]) => <TableCell key={label}>{label}</TableCell>)}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.rows.slice(0, 50).map((row: any, index: number) => (
                          <TableRow key={index}>
                            {report.headers.map(([label, key]) => <TableCell key={label}>{row[key] ?? ''}</TableCell>)}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {report.rows.length > 50 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                      Showing first 50 of {report.rows.length} rows. Export to see all data.
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Report History</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Report Type</TableCell>
                  <TableCell>Generated By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center">No report history</TableCell></TableRow>
                ) : history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{formatDate(item.generatedAt)}</TableCell>
                    <TableCell>{REPORT_LABELS[item.reportType as ReportType] || item.reportType}</TableCell>
                    <TableCell>{item.generatedBy}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => {
                        setReportType(item.config.reportType);
                        setStartDate(item.config.startDate || '');
                        setEndDate(item.config.endDate || '');
                        setCourseFilter(item.config.courseFilter || 'all');
                        setDepartmentFilter(item.config.departmentFilter || 'all');
                        setHistoryOpen(false);
                      }}>Reload</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingReportsPage;
